import { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const load = (key, fallback) => {
  try {
    const v = localStorage.getItem(key);
    return v ? JSON.parse(v) : fallback;
  } catch {
    return fallback;
  }
};

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => load("sulbel_cart", []));
  const [favorites, setFavorites] = useState(() => load("sulbel_favs", []));
  const [coupon, setCoupon] = useState(null);

  useEffect(() => {
    localStorage.setItem("sulbel_cart", JSON.stringify(items));
  }, [items]);
  useEffect(() => {
    localStorage.setItem("sulbel_favs", JSON.stringify(favorites));
  }, [favorites]);

  const addToCart = (product, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.product.id === product.id);
      if (found) {
        return prev.map((i) =>
          i.product.id === product.id ? { ...i, qty: i.qty + qty } : i
        );
      }
      return [...prev, { product, qty }];
    });
  };

  const removeFromCart = (id) =>
    setItems((prev) => prev.filter((i) => i.product.id !== id));

  const updateQty = (id, qty) =>
    setItems((prev) =>
      prev.map((i) => (i.product.id === id ? { ...i, qty: Math.max(1, qty) } : i))
    );

  const clearCart = () => setItems([]);

  const toggleFavorite = (id) =>
    setFavorites((prev) =>
      prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id]
    );

  const isFavorite = (id) => favorites.includes(id);

  const applyCoupon = (code) => {
    const c = code.trim().toUpperCase();
    if (c === "SULBEL10") {
      setCoupon({ code: c, percent: 10 });
      return true;
    }
    return false;
  };

  const count = items.reduce((a, i) => a + i.qty, 0);
  const subtotal = items.reduce((a, i) => a + i.product.price * i.qty, 0);
  const shipping = items.length === 0 ? 0 : subtotal >= 300 ? 0 : 39.9;
  const discount = coupon ? (subtotal * coupon.percent) / 100 : 0;
  const total = Math.max(0, subtotal + shipping - discount);

  const value = useMemo(
    () => ({
      items, favorites, coupon,
      addToCart, removeFromCart, updateQty, clearCart,
      toggleFavorite, isFavorite, applyCoupon, setCoupon,
      count, subtotal, shipping, discount, total,
    }),
    [items, favorites, coupon] // eslint-disable-line
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export const useCart = () => useContext(CartContext);
